import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { Card, Button, Badge, Modal, Select } from '../components/UI';

const ClinicDashboard = () => {
  const navigate = useNavigate();
  const { user, isTestMode } = useAuth();
  const { getSubscription, changePlan } = useSubscription();
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  
  const [showSubModal, setShowSubModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('standard');

  const subscription = getSubscription(user?.id);

  const handleChangePlan = () => {
    changePlan(user?.id, selectedPlan);
    setShowSubModal(false);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('dashboard')} - {t('clinic')}
          </h1>
          <p className="text-gray-600 mt-2">{t('welcome')}, {user?.name}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Subscription Card */}
          <Card title={t('subscription')}>
            <div className="space-y-3">
              <div>
                <Badge variant="info" className="text-base">
                  {t(subscription.plan)}
                </Badge>
              </div>
              <div className="text-sm text-gray-600">
                <p>{t('postsUsed')}: {subscription.postsUsed} / {subscription.postsLimit}</p>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className="bg-gold-500 h-2 rounded-full"
                    style={{ width: `${Math.min((subscription.postsUsed / subscription.postsLimit) * 100, 100)}%` }}
                  />
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full text-sm"
                onClick={() => {
                  if (isTestMode) {
                    setShowSubModal(true);
                  } else {
                    navigate('/placeholder/upgrade');
                  }
                }}
              >
                {t('upgradePlan')}
              </Button>
            </div>
          </Card>

          {/* Quick Stats */}
          <Card title={t('activeShifts')}>
            <div className="text-center">
              <div className="text-4xl font-bold text-gold-600">0</div>
              <p className="text-gray-600 text-sm mt-2">{t('inProgress')}</p>
            </div>
          </Card>

          <Card title={t('notifications')}>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">0</div>
              <p className="text-gray-600 text-sm mt-2">{t('newMessages')}</p>
              <Button 
                variant="outline" 
                className="w-full text-sm mt-3"
                onClick={() => navigate('/placeholder/notifications')}
              >
                {t('notifications')}
              </Button>
            </div>
          </Card>
        </div>

        {/* Main Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold mb-4">{t('postShift')}</h3>
            <p className="text-gray-600 mb-4">
              {t('publishNewShift')}
            </p>
            <Button 
              className="w-full"
              onClick={() => navigate('/liberado-jefe-clinic/post-shift')}
            >
              + {t('postShift')}
            </Button>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">{t('shiftTemplates')}</h3>
            <p className="text-gray-600 mb-4">
              {t('saveTemplates')}
            </p>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/placeholder/templates')}
            >
              {t('viewTemplates')}
            </Button>
          </Card>
        </div>

        {/* AI Recommendation Section */}
        <Card className="mt-6 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-300">
          <div className="flex items-center mb-4">
            <svg className="w-6 h-6 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            <h3 className="text-lg font-semibold text-purple-900">
              {language === 'es' ? 'Recomendación de IA – Técnicos sugeridos' : language === 'en' ? 'AI Recommendation – Suggested Technicians' : 'Recomendação de IA – Técnicos sugeridos'}
            </h3>
          </div>
          <p className="text-sm text-gray-700 mb-4">
            {language === 'es' ? 'Basado en proximidad, experiencia, disponibilidad y habilidades clínicas coincidentes.' : language === 'en' ? 'Based on proximity, experience, availability and matching clinical skills.' : 'Baseado em proximidade, experiência, disponibilidade e habilidades clínicas correspondentes.'}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-lg p-4 border border-purple-200">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-semibold text-gray-900">
                      {language === 'es' ? 'Técnico ' : language === 'en' ? 'Technician ' : 'Técnico '}{i}
                    </div>
                    <div className="text-xs text-gray-500">
                      {language === 'es' ? 'Certificado' : language === 'en' ? 'Certified' : 'Certificado'}
                    </div>
                  </div>
                  <Badge variant="success" className="text-xs">
                    {92 - i}% Match
                  </Badge>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>✓ {language === 'es' ? '5 años exp.' : language === 'en' ? '5 years exp.' : '5 anos exp.'}</div>
                  <div>✓ {language === 'es' ? '2.5 km' : '2.5 km'}</div>
                  <div>✓ {language === 'es' ? 'Disponible' : language === 'en' ? 'Available' : 'Disponível'}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Regular shifts list */}
        <Card className="mt-6">
          <h3 className="text-lg font-semibold mb-4">{t('activeShifts')}</h3>
          <div className="text-center py-12 text-gray-400">
            <p>{t('noActiveShifts')}</p>
            <p className="text-sm mt-2">{t('shiftsWillAppear')}</p>
          </div>
        </Card>
      </div>

      {/* Subscription Change Modal */}
      <Modal
        isOpen={showSubModal}
        onClose={() => setShowSubModal(false)}
        title={t('changeSubscription')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 mb-4">
            {isTestMode && '⚠️ ' + t('testModeActive')}
          </p>
          <Select
            label={t('subscription')}
            value={selectedPlan}
            onChange={(e) => setSelectedPlan(e.target.value)}
            options={[
              { value: 'standard', label: t('standard') + ' - 5 posts/month' },
              { value: 'pro', label: t('pro') + ' - 15 posts/month' },
              { value: 'enterprise', label: t('enterprise') + ' - Unlimited' },
            ]}
          />
          <div className="flex gap-2">
            <Button onClick={handleChangePlan} className="flex-1">
              {t('save')}
            </Button>
            <Button onClick={() => setShowSubModal(false)} variant="secondary" className="flex-1">
              {t('cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default ClinicDashboard;